import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import moment from 'moment';
import { RxUpdate } from 'react-icons/rx';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useVacationRequestActions from '../../../hooks/APIHooks/users/useVacationRequestActions';
import { VacationReqBody, VacationRequest } from '../../../types/vacations';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import { compareTwoDates } from '../../../utils/timeAndDate';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaDatePicker from '../Inputs/AcademiaDatePicker';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const CreateEditVacationRequestModal = ({
  title,
  vacationRequest,
  handleModalClose,
}: {
  title: 'create' | 'update';
  vacationRequest?: VacationRequest;
  handleModalClose: () => void;
}) => {
  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { createNewVacationRequestHandler, updateVacationRequestHandler } =
    useVacationRequestActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to create/update vacation request declared here
  const createUpdateVacationRequestHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log(values);

      let body: VacationReqBody = {
        start_date: moment(values.start_date, 'DD-MM-YYYY').format(
          'YYYY-MM-DD'
        ),
        end_date: moment(values.end_date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
        description: values.description,
        user: currentUser?.id as number,
      };

      if (!vacationRequest) {
        body = { ...body, created_by: currentUser?.username };

        await createNewVacationRequestHandler
          .mutateAsync({ body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Request Created!!');

              handleModalClose();
              queryClient.invalidateQueries({
                queryKey: ['vacation-request-list'],
              });
            } else {
              toast.error(res.message || 'Request Creation Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      } else {
        body = { ...body, updated_by: currentUser?.username };

        await updateVacationRequestHandler
          .mutateAsync({ vacReqId: vacationRequest.id, body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Request Updated!!');

              handleModalClose();
              queryClient.invalidateQueries({
                queryKey: ['vacation-request-list'],
              });
            } else {
              toast.error(res.message || 'Request Updating Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering create/edit vacation request modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText={`${title} vacation request`} />

      <Formik
        initialValues={{
          start_date: vacationRequest?.start_date || '',
          end_date: vacationRequest?.end_date || '',
          description: vacationRequest?.description || '',
        }}
        onSubmit={createUpdateVacationRequestHandler}
      >
        {(props) => (
          <Form className='space-y-5'>
            <AcademiaDatePicker
              label='Starting Date'
              name='start_date'
              minDate={new Date()}
              defaultDate={
                props.values.start_date
                  ? new Date(props.values.start_date)
                  : new Date()
              }
              alterDateFieldName={
                compareTwoDates(
                  props.values.start_date,
                  props.values.end_date
                ) > 0
                  ? 'end_date'
                  : ''
              }
              placeholder='DD-MM-YYYY'
              isRequired
            />

            <AcademiaDatePicker
              label='Ending Date'
              name='end_date'
              minDate={
                props.values.start_date
                  ? new Date(
                      moment(props.values.start_date, 'DD-MM-YYYY').format(
                        'YYYY-MM-DD'
                      )
                    )
                  : new Date()
              }
              defaultDate={
                props.values.end_date
                  ? new Date(props.values.end_date)
                  : new Date()
              }
              placeholder='DD-MM-YYYY'
            />

            <AcademiaTextAreaField
              name='description'
              label='Vacation Description'
              placeholder='Write your reasons for vacation here...'
              isRequired
            />

            <AcademiaButton
              type='submit'
              variant='premium'
              icon={
                title === 'create' ? (
                  <TbSquareRoundedPlusFilled className='size-6' />
                ) : (
                  <RxUpdate className='size-6' />
                )
              }
              isLoading={props.isSubmitting}
              fullWidth
            >
              {title} Vacation Request
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default CreateEditVacationRequestModal;
